// @owners { team: patients }
import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { SPACING } from '@alto/design-library-tokens';
import { Body, Column, H3, LgPadding } from '@alto/design-system';
import React from 'react';
import { Platform } from 'react-native';
import { getCompanyFullName } from '~shared/config/getCompanyName';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { getPrescriptionsByIDs } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { isMail } from '~shared/helpers/order';
import { useSelectorShared } from '~shared/store';

type Props = {
  readonly orderIndex: number;
};

export const CheckoutSplitShipmentHeader = ({ orderIndex }: Props) => {
  const { cartItemsByDeliveryMethod, deliveryMethods } = useCartDeliveryMethods();
  const deliveryMethod: DeliveryMethod | undefined = deliveryMethods[orderIndex];
  const deliveryMethodName = deliveryMethod?.name;
  const prescriptionItemsForShipment = deliveryMethodName ? (cartItemsByDeliveryMethod[deliveryMethodName] ?? []) : [];
  const prescriptionIds = prescriptionItemsForShipment.map((item) => item.resource_id);
  const prescriptions = useSelectorShared((state) => getPrescriptionsByIDs(state, prescriptionIds));
  const shipmentHasCompounds = prescriptions.find((prescription) => prescription.is_compound);
  const companyFullName = getCompanyFullName();

  const deliveredFrom = shipmentHasCompounds && orderIndex === 0 ? 'compound pharmacy' : companyFullName;
  const methodName = isMail(deliveryMethod?.mode) ? 'Mail' : deliveryMethod?.patient_facing_name;
  const deliveryMethodLabel = `${methodName} from ${deliveredFrom}`;

  if (Platform.OS === 'web') {
    return (
      <Column wrap={false}>
        <H3>
          Delivery {orderIndex + 1} of {deliveryMethods.length}
        </H3>
        <LgPadding
          topPadding={SPACING.STATIC.NONE}
          leftPadding={SPACING.STATIC.NONE}
        >
          <Body>{deliveryMethodLabel}</Body>
        </LgPadding>
      </Column>
    );
  }

  return (
    <Column center>
      <H3>
        Delivery {orderIndex + 1} of {deliveryMethods.length}
      </H3>
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Body center>{deliveryMethodLabel}</Body>
      </LgPadding>
    </Column>
  );
};
